import { useContextSelector } from 'use-context-selector';
import type { ComputeContextMode } from '../../../hooks/computeContext';
import type useBlockEvents from '../../../hooks/useBlockEvents';
import {
  DataSourceControllerContext,
  type DataSourceControllerContextType,
} from './DataSourceControllerContext';

const useDataSourceControllerSelector = <T>(
  selector: (context: DataSourceControllerContextType) => T,
): T => {
  return useContextSelector(DataSourceControllerContext, (context) => {
    if (!context) throw new Error('DataSourceControllerContext is undefined');
    return selector(context);
  });
};

export const useDSCBlocks = () => {
  return useDataSourceControllerSelector((context) => context.blocks);
};

export const useDSCEntityTypeDataSources = () => {
  return useDataSourceControllerSelector((context) => context.entityTypeDataSources);
};

export const useDSCDataSourcesFromPage = () => {
  return useDataSourceControllerSelector((context) => context.dataSourcesFromPage);
};

export const useDSCPageMetadata = () => {
  return useDataSourceControllerSelector((context) => context.metadata);
};

export const useDSCBlockLifecycles = () => {
  return useDataSourceControllerSelector((context) => context.blockLifecycles);
};

export const useDSCMode = () => {
  return useDataSourceControllerSelector((context) => context.mode);
};

export const useDSCManagerId = () => {
  return useDataSourceControllerSelector((context) => context.managerId);
};

export const useDSCIsPublicPage = () => {
  return useDataSourceControllerSelector((context) => context.isPublicPage);
};

export const useDSCGetComputeContext = () => {
  return useDataSourceControllerSelector((context) => context.getComputeContext);
};

export const useDSCUseNonReactiveComputeContext = () => {
  return useDataSourceControllerSelector((context) => context.useNonReactiveComputeContext);
};

export const useDSCUseReactiveComputeContext = ({ mode }: { mode?: ComputeContextMode } = {}) => {
  return useDataSourceControllerSelector((context) => context.useReactiveComputeContext)({ mode });
};

export const useDSCDataSourceRecordStore = () => {
  return useDataSourceControllerSelector((context) => context.useDataSourceRecordStore);
};

export const useDSCGlobalStateStore = () => {
  return useDataSourceControllerSelector((context) => context.useGlobalStateStore);
};

export const useDSCGetState = () => {
  return useDSCGlobalStateStore().getState;
};

export const useDSCDependencyGraphProvider = () => {
  return useDataSourceControllerSelector((context) => context.useDependencyGraphProvider);
};

export const useDSCActions = () => {
  return useDSCGlobalStateStore().hooks.useActions().dataSource;
};

export const useDSCBlockEvents = (...args: Parameters<typeof useBlockEvents>) => {
  return useDataSourceControllerSelector((context) => context.useBlockEvents)(...args);
};

export const useDSCInterfaceRecord = () => {
  return useDataSourceControllerSelector((context) => context.interfaceRecord);
};

export const useDSCAllowedDataSourceResourceNames = () => {
  return useDataSourceControllerSelector((context) => context.allowedDataSourceResourceNames);
};

export const useDSCActivePageSlug = () => {
  return useDataSourceControllerSelector((context) => context.activePageSlug);
};
